import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { history } from '../../helpers/history';
import { useRecoilValue } from 'recoil';
import { authAtom } from '../../states/usuarios/AuthState';
import { useUsuarioActions } from '../../states/usuarios/usuario.actions';
import { useTranslation } from '../../commons/useTranslasion';
import { useMediaQuery, useTheme } from '@mui/material';

// Importando componentes refatorados
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import { MainContainer, ContentContainer } from './StyledComponents';
import { IModulo } from './types';

// Exportações necessárias para manter compatibilidade
export { default as Header } from './components/Header';
export { default as Sidebar } from './components/Sidebar';
export * from './StyledComponents';
export * from './types';
export * from './constants';

/**
 * Componente principal de Layout
 */
const Layout: React.FC = (props: any) => {
  const { children } = props;
  const { translate } = useTranslation();
  const theme = useTheme();
  const path = window.location.pathname;
  const usuarioActions = useUsuarioActions();
  const auth = useRecoilValue(authAtom);
  
  // Estado para controlar se o menu está aberto ou não
  const [open, setOpen] = useState(true);
  
  // Verifica se é dispositivo móvel
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Estado local para rastrear o título da página atual
  const [paginaAtual, setPaginaAtual] = useState<string>('');
  
  // Efeito para configurar o estado inicial baseado em mobile/desktop
  useEffect(() => {
    // No mobile, o menu começa fechado; no desktop, começa aberto
    setOpen(!isMobile);
  }, [isMobile]);

  // Efeito para atualizar o título da página baseado na rota atual
  useEffect(() => {
    const pagina = menuItens.find(item => 
      item.link === path || 
      (path.startsWith(item.link) && item.link !== '/')
    );
    if (pagina) {
      setPaginaAtual(pagina.nome);
    } else if (path === '/') {
      setPaginaAtual(translate('dashboard'));
    }
  }, [path, translate]);

  // Função para alternar o estado do menu no desktop
  const handleDrawerClose = () => {
    setOpen(!open);
  };

  // Função para abrir/fechar o menu no mobile
  const handleMobileMenuToggle = () => {
    setOpen(!open);
  };

  // Função para navegar para outra página ao clicar em um item de menu
  const handleMenuClick = (url: string) => {
    history.push(url);
    // No mobile, fecha o menu ao navegar
    if (isMobile) {
      setOpen(false);
    }
  };

  // Função para realizar logout
  const handleLogout = () => {
    usuarioActions.logout();
    history.push('/login');
  };

  // Itens do menu
  const menuItens: IModulo[] = [
    {
      privilegio: '',
      icon: 'dashboardRounded',
      nome: translate('dashboard'),
      link: '/',
    },
    {
      privilegio: '',
      icon: 'eventNoteRounded',
      nome: translate('reunioes'),
      link: '/reunioes',
    },
    {
      privilegio: '',
      icon: 'groupsRounded',
      nome: translate('assessorados'),
      link: '/assessorados',
    },
    {
      privilegio: '',
      icon: 'folderRounded',
      nome: translate('novoCurriculo'),
      link: '/novo-curriculo',
    },
    {
      privilegio: '',
      icon: 'settingsRounded',
      nome: translate('timesheet'),
      link: '/timeSheet',
    },
  ];

  return (
    <>
      {/* Header superior */}
      <Header 
        open={open}
        isMobile={isMobile}
        handleMobileMenuToggle={handleMobileMenuToggle}
        paginaAtual={paginaAtual}
        handleMenuClick={handleMenuClick}
      />
      
      {/* Sidebar lateral */}
      <Sidebar 
        open={open}
        isMobile={isMobile}
        menuItens={menuItens}
        path={path}
        handleDrawerClose={handleDrawerClose}
        handleMobileMenuToggle={handleMobileMenuToggle}
        handleMenuClick={handleMenuClick}
        handleLogout={handleLogout}
      />
      
      {/* Conteúdo principal */}
      <MainContainer open={open} isMobile={isMobile}>
        <ContentContainer container>
          {children}
        </ContentContainer>
      </MainContainer>
    </>
  );
};

// Exportação padrão do componente Menu com withRouter
export default withRouter(Layout);
